import api from './api';
import { Provider } from '../types/provider';

export interface ProviderPublic {
  id: number;
  business_name: string;
  specialties: string[];
  bio: string;
  years_of_experience: number;
  city: string;
  state: string;
  clout_score: number;
  is_certified: number;
  profile_image_url: string | null;
  username: string;
}

export interface ProviderWithUser extends Provider {
  // Not returned by the public provider endpoint
  user_email?: string | null;
  user_username: string;
  user_profile_image_url: string | null;
}

export interface ProviderOverviewStats {
  endorsements_received: number;
  endorsements_given: number;
  endorsed_by_top_scorers: number;
}

export const providersApi = {
  // Get top providers by CLOUT score
  getTopProviders: async (limit: number = 10): Promise<ProviderPublic[]> => {
    const response = await api.get(`/api/providers/top-scorers?limit=${limit}`);
    return response.data;
  },

  // Get a specific provider by ID
  getProvider: async (providerId: number): Promise<ProviderWithUser> => {
    const response = await api.get(`/api/providers/${providerId}`);
    return response.data;
  },

  // Search providers
  searchProviders: async (
    query: string,
    skip: number = 0,
    limit: number = 100
  ): Promise<ProviderPublic[]> => {
    const response = await api.get(
      `/api/providers/search?q=${encodeURIComponent(query)}&skip=${skip}&limit=${limit}`
    );
    return response.data;
  },

  // Get certified providers
  getCertifiedProviders: async (
    skip: number = 0,
    limit: number = 100
  ): Promise<ProviderPublic[]> => {
    const response = await api.get(`/api/providers/certified?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  // Get current user's provider profile
  getMyProfile: async (): Promise<ProviderWithUser> => {
    const response = await api.get('/api/providers/me');
    return response.data;
  },

  // Update current user's provider profile
  updateMyProfile: async (data: Partial<Provider>): Promise<Provider> => {
    const response = await api.put('/api/providers/me', data);
    return response.data;
  },

  // Get overview statistics for current user's provider profile
  getMyOverviewStats: async (): Promise<ProviderOverviewStats> => {
    const response = await api.get('/api/providers/me/overview-stats');
    return response.data;
  },

  // Get nearby providers
  getNearbyProviders: async (limit: number = 6): Promise<ProviderPublic[]> => {
    const response = await api.get(`/api/providers/nearby?limit=${limit}`);
    return response.data;
  },

  // Get search suggestions
  getSearchSuggestions: async (query: string): Promise<string[]> => {
    if (query.length < 2) return [];
    const response = await api.get(
      `/api/providers/search/suggestions?q=${encodeURIComponent(query)}`
    );
    return response.data;
  },

  // Get current user's provider profile
  getCurrentProvider: async (): Promise<ProviderWithUser> => {
    const response = await api.get('/api/providers/me');
    return response.data;
  },
};
